import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Stack,
  InputAdornment,
  Divider,
  Chip,
  Paper,
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Warning, 
  Timer, 
  Speed,
  AttachMoney,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { Recommendation, Position, Portfolio } from '../types';
import { PositionService } from '../services/positionService';
import { RecommendationService } from '../services/recommendationService';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { format } from 'date-fns';

interface RecommendationExitDialogProps {
  open: boolean;
  onClose: () => void;
  recommendation: Recommendation | null;
  position: Position | null;
  portfolio: Portfolio | null;
  onPositionClosed: () => void;
}

const RecommendationExitDialog: React.FC<RecommendationExitDialogProps> = ({
  open,
  onClose,
  recommendation,
  position,
  portfolio,
  onPositionClosed,
}) => {
  const [exitPrice, setExitPrice] = useState('');
  const [exitDate, setExitDate] = useState(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alpacaStatus, setAlpacaStatus] = useState<string | null>(null);

  useEffect(() => {
    if (recommendation && position) {
      // Pre-fill with current price from recommendation
      setExitPrice(recommendation.currentPrice.toFixed(2));
      setExitDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    }
  }, [recommendation, position]);

  const handleClose = () => {
    setExitPrice('');
    setExitDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setError(null);
    setAlpacaStatus(null);
    onClose();
  };

  const handleClosePosition = async () => {
    if (!position || !recommendation) return;

    setError(null);
    setLoading(true);

    try {
      const price = parseFloat(exitPrice);

      if (isNaN(price) || price <= 0) {
        throw new Error('Please enter a valid exit price');
      }

      if (portfolio?.alpacaLinked) {
        // Route through Alpaca Cloud Function
        setAlpacaStatus('Submitting sell to Alpaca...');
        const executeAlpacaTrade = httpsCallable<{
          side: 'buy' | 'sell';
          symbol: string;
          qty: number;
          portfolioId: string;
          recommendationId: string;
          positionId: string;
        }, {
          success: boolean;
          filledPrice: number;
          filledQty: number;
          orderId: string;
        }>(functions, 'executeAlpacaTrade');

        setAlpacaStatus('Waiting for Alpaca fill...');
        const result = await executeAlpacaTrade({
          side: 'sell',
          symbol: position.symbol,
          qty: position.quantity,
          portfolioId: position.portfolioId,
          recommendationId: recommendation.id,
          positionId: position.id,
        });

        setAlpacaStatus(`Sold: ${result.data.filledQty} shares @ $${result.data.filledPrice.toFixed(2)}`);
        // Cloud function handles all Firestore writes + recommendation marking
      } else {
        // Non-Alpaca: write directly to Firestore
        const exitDateObj = new Date(exitDate);
        await PositionService.closePosition(
          position.id,
          price,
          exitDateObj
        );

        // Mark recommendation as executed
        await RecommendationService.markRecommendationExecuted(recommendation.id);
      }

      onPositionClosed();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to close position');
    } finally {
      setLoading(false);
      setAlpacaStatus(null);
    }
  };

  if (!recommendation || !position || !portfolio) return null;

  const exitPriceNum = parseFloat(exitPrice) || 0;
  const totalProceeds = exitPriceNum * position.quantity;
  const totalCost = position.entryPrice * position.quantity;
  const gainLoss = totalProceeds - totalCost;
  const gainLossPercent = totalCost > 0 ? (gainLoss / totalCost) * 100 : 0;

  // Get exit reason icon and color
  const getExitReasonDetails = () => {
    switch (recommendation.exitReason) {
      case 'stop_hit':
        return {
          icon: <Warning />,
          color: 'error' as const,
          title: 'Stop Loss Hit',
          description: 'Price has reached or exceeded your stop loss level'
        };
      case 'target_hit':
        return {
          icon: <TrendingUp />,
          color: 'success' as const,
          title: 'Target Reached',
          description: 'Price has reached your profit target'
        };
      case 'time_limit':
        return {
          icon: <Timer />,
          color: 'warning' as const,
          title: 'Holding Time Exceeded',
          description: `Position held for ${position.holdingDays} days`
        };
      case 'stagnant':
        return {
          icon: <Speed />,
          color: 'info' as const,
          title: 'Position Stagnant',
          description: 'Limited price movement - consider freeing up capital'
        };
      default:
        return {
          icon: <AttachMoney />,
          color: 'default' as const,
          title: 'Exit Recommendation',
          description: 'Consider closing this position'
        };
    }
  };

  const exitDetails = getExitReasonDetails();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">Exit Position - {position.symbol}</Typography>
              <Chip
                icon={exitDetails.icon}
                label={exitDetails.title}
                color={exitDetails.color}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              {position.quantity} shares @ {formatCurrency(position.entryPrice)} entry
            </Typography>
          </Box>
          <Chip
            label={`Score: ${recommendation.score}`}
            size="small"
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {alpacaStatus && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {alpacaStatus}
            </Alert>
          )}

          {/* Exit Reason Alert */}
          <Alert 
            severity={exitDetails.color === 'default' ? 'info' : exitDetails.color} 
            icon={exitDetails.icon}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" fontWeight="medium">
              {exitDetails.title}
            </Typography>
            <Typography variant="body2">
              {exitDetails.description}
            </Typography>
          </Alert>

          {/* Recommendation Reasons */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
              Recommendation Reasons:
            </Typography>
            {recommendation.reasons.map((reason, i) => (
              <Typography key={i} variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                • {reason}
              </Typography>
            ))}
          </Paper>

          <TextField
            fullWidth
            label="Exit Price"
            type="number"
            value={exitPrice}
            onChange={(e) => setExitPrice(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { step: 0.01, min: 0 }
            }}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Exit Date/Time"
            type="datetime-local"
            value={exitDate}
            onChange={(e) => setExitDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Trade Summary
          </Typography>
          
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary">
                Entry Cost:
              </Typography>
              <Typography variant="body2">
                {formatCurrency(totalCost)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary">
                Current Price:
              </Typography>
              <Typography variant="body2">
                {formatCurrency(recommendation.currentPrice)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary">
                Exit Proceeds:
              </Typography>
              <Typography variant="body2">
                {formatCurrency(totalProceeds)}
              </Typography>
            </Box>
            
            <Divider />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="medium">
                Gain/Loss:
              </Typography>
              <Typography 
                variant="body1" 
                fontWeight="medium"
                color={gainLoss >= 0 ? 'success.main' : 'error.main'}
              >
                {gainLoss >= 0 ? '+' : ''}
                {formatCurrency(gainLoss)} ({formatPercent(gainLossPercent)})
              </Typography>
            </Box>

            {/* Position Details */}
            <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 2 }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="textSecondary">
                    Holding Period:
                  </Typography>
                  <Typography variant="caption">
                    {position.holdingDays} days
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="textSecondary">
                    Stop Loss:
                  </Typography>
                  <Typography variant="caption">
                    {formatCurrency(position.stopLoss)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="textSecondary">
                    Target:
                  </Typography>
                  <Typography variant="caption">
                    {formatCurrency(position.targetPrice)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
            
            <Alert 
              severity="info" 
              sx={{ mt: 2 }}
              icon={false}
            >
              <Typography variant="body2">
                <strong>Fund Impact:</strong><br />
                • Available Funds will increase by {formatCurrency(totalProceeds)}<br />
                • Portfolio earnings will {gainLoss >= 0 ? 'increase' : 'decrease'} by {formatCurrency(Math.abs(gainLoss))}
              </Typography>
            </Alert>
          </Stack>

          {/* Technical Factors if available */}
          {recommendation.technicalFactors && Object.keys(recommendation.technicalFactors).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="textSecondary">
                Technical Factors: {Object.entries(recommendation.technicalFactors)
                  .map(([key, value]) => `${key}: ${typeof value === 'number' ? value.toFixed(2) : value}`)
                  .join(' | ')}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleClosePosition}
          disabled={loading || !exitPrice || exitPriceNum <= 0}
          color={gainLoss >= 0 ? 'success' : 'error'}
          startIcon={exitDetails.icon}
        >
          {portfolio?.alpacaLinked ? 'Submit Alpaca Sell' : 'Close Position'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecommendationExitDialog;